<template>
  <div>
    <template v-for="(segment, i) in segments">
      <div
        v-if="segment.kind === 'markdown'"
        :key="`md-${i}`"
        class="markdown-content"
        @click="handleClick"
        v-html="segment.html"
      />
      <ChartView
        v-else
        :key="`chart-${i}`"
        :spec="segment.spec"
        :document-index="documentIndex"
        @ref-click="onChildRefClick"
      />
    </template>
  </div>
</template>

<script>
import { computed, defineComponent } from 'vue';
import MarkdownIt from 'markdown-it';
import ChartView from './charts/ChartView.vue';
import { parseChartSpec } from './chartSpec.js';

// ── Markdown renderer ─────────────────────────────────
// `html: false` → raw HTML in source is escaped. `linkify` turns bare URLs
// into <a>. Scoped styles below cover all typography.
const md = new MarkdownIt({
  html: false,
  linkify: true,
  breaks: true,
  typographer: true,
});

// ── Chart block detection ─────────────────────────────
// Match a ```chart fenced block. We split the source on this pattern and
// render each split as either a markdown segment or a chart segment,
// preserving order. `[\s\S]` instead of `.` so multi-line JSON parses.
const CHART_BLOCK_PATTERN = /```chart\s*\n([\s\S]*?)\n```/g;

// ── RM reference chip post-processing ─────────────────
const RM_REFERENCE_PATTERN = /\b(RM-[A-Za-z0-9-]+)\b/;
const RM_REFERENCE_PATTERN_G = /\b(RM-[A-Za-z0-9-]+)\b/g;
const SKIP_PARENT_TAGS = new Set(['CODE', 'PRE', 'A', 'BUTTON']);

// Portal deep links to a document (the tools stamp `portal_url` =
// …/#/viewdoc?id=RM-xxx on results for EXTERNAL hosts). Inside the
// portal they must behave like reference chips — open the in-app
// summary popup — not navigate the browser to a second copy of the app.
const VIEWDOC_HREF_PATTERN = /viewdoc\?id=(RM-[A-Za-z0-9-]+)/;

// Brand pillars Atlas is expert in — `**bold**` mentions of these four
// words are recoloured in the indigo accent so they align with the
// header lockup across the app.
const ATLAS_PILLAR_WORDS = new Set([
  'Architecture',
  'Transparency',
  'Levelling',
  'Skills',
]);
const ATLAS_ACCENT_TAG_PATTERN =
  /<strong>(?:Architecture|Transparency|Levelling|Skills)<\/strong>/;

function accentAtlasPillars(root) {
  // Accent ONLY when the full brand lockup is present — all four pillar
  // words bold in the same message (the greeting's signature). A lone
  // bold "Skills" in an ordinary answer is just a section name and must
  // stay ink-coloured (live issue 2026-09-02: capability answers listing
  // document sections got a stray indigo "Skills").
  const matches = Array.from(root.querySelectorAll('strong')).filter((strong) =>
    ATLAS_PILLAR_WORDS.has((strong.textContent || '').trim()),
  );
  const distinctWords = new Set(matches.map((s) => (s.textContent || '').trim()));
  if (distinctWords.size < ATLAS_PILLAR_WORDS.size) return;
  for (const strong of matches) {
    strong.classList.add('atlas-accent-word');
  }
}

/**
 * Walk rendered HTML and convert:
 *   1. <code>RM-xxx</code> (preferred — Claude is told to use backticks) → chip
 *   2. bare RM-xxx inside text nodes not nested in code/pre/a → chip
 * Uses DOMParser rather than HTML regex to avoid attribute/tag footguns.
 *
 * `documentIndex` (optional) maps `RM-xxx` → `{ doc_id, doc_name }`; when a
 * chip's reference is found, the chip gets `data-doc-id` / `data-doc-name`
 * stamped and its `title` set to the doc name. The click handler reads
 * all three off the DOM so consumers receive a fully structured payload
 * without a second lookup. Refs missing from the index still produce a
 * working chip — they just emit with null doc fields.
 */
function enhanceReferences(html, documentIndex) {
  // Skip the DOM round-trip unless an enhancement applies.
  const needsRefPass = RM_REFERENCE_PATTERN.test(html);
  const needsAccentPass = ATLAS_ACCENT_TAG_PATTERN.test(html);
  const needsLinkPass = html.includes('viewdoc?id=');
  if (!needsRefPass && !needsAccentPass && !needsLinkPass) return html;

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div id="rm-root">${html}</div>`, 'text/html');
  const root = doc.getElementById('rm-root');
  if (!root) return html;

  if (needsAccentPass) accentAtlasPillars(root);
  if (needsLinkPass) rewriteViewdocLinks(root, documentIndex);
  if (!needsRefPass) return root.innerHTML;

  // Convert <code>RM-xxx</code> whose text is a pure reference.
  for (const code of Array.from(root.querySelectorAll('code'))) {
    const text = code.textContent || '';
    const m = text.match(/^(RM-[A-Za-z0-9-]+)$/);
    if (m) code.replaceWith(makeChip(doc, m[1], documentIndex));
  }

  // Walk text nodes, replace bare RM-xxx matches.
  const textNodes = [];
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let current = walker.nextNode();
  while (current) {
    textNodes.push(current);
    current = walker.nextNode();
  }
  for (const textNode of textNodes) {
    let p = textNode.parentElement;
    let skip = false;
    while (p && p !== root) {
      if (SKIP_PARENT_TAGS.has(p.tagName)) {
        skip = true;
        break;
      }
      p = p.parentElement;
    }
    if (skip) continue;

    const text = textNode.textContent || '';
    if (!RM_REFERENCE_PATTERN.test(text)) continue;

    const parts = text.split(RM_REFERENCE_PATTERN_G);
    const frag = doc.createDocumentFragment();
    for (const part of parts) {
      if (RM_REFERENCE_PATTERN.test(part) && /^RM-[A-Za-z0-9-]+$/.test(part)) {
        frag.appendChild(makeChip(doc, part, documentIndex));
      } else if (part) {
        frag.appendChild(doc.createTextNode(part));
      }
    }
    textNode.parentNode && textNode.parentNode.replaceChild(frag, textNode);
  }

  return root.innerHTML;
}

/**
 * Turn portal viewdoc anchors into in-app doc links: strip the href
 * (no navigation) and stamp `data-ref`, so handleClick routes them
 * through the SAME ref-click path as reference chips — the summary
 * popup opens anchored at the link. A linkified BARE url (visible text
 * is the url itself) swaps its text for the doc name / reference so no
 * raw url ever renders in a bubble.
 */
function rewriteViewdocLinks(root, documentIndex) {
  for (const a of Array.from(root.querySelectorAll('a[href*="viewdoc?id="]'))) {
    const m = (a.getAttribute('href') || '').match(VIEWDOC_HREF_PATTERN);
    if (!m) continue;
    const reference = m[1];
    a.removeAttribute('href');
    a.removeAttribute('target');
    a.classList.add('rm-doc-link');
    a.setAttribute('data-ref', reference);
    a.setAttribute('role', 'button');
    a.setAttribute('tabindex', '0');
    const match = documentIndex ? documentIndex.get(reference) : null;
    if (match) {
      if (match.doc_id != null) a.setAttribute('data-doc-id', String(match.doc_id));
      if (match.doc_name) a.setAttribute('data-doc-name', match.doc_name);
    }
    const text = (a.textContent || '').trim();
    if (/^https?:\/\//i.test(text)) {
      a.textContent = (match && match.doc_name) || reference;
    }
  }
}

function makeChip(doc, reference, documentIndex) {
  const btn = doc.createElement('button');
  btn.type = 'button';
  btn.className = 'rm-ref-chip';
  btn.setAttribute('data-ref', reference);
  btn.textContent = reference;
  const match = documentIndex ? documentIndex.get(reference) : null;
  if (match) {
    if (match.doc_id != null) btn.setAttribute('data-doc-id', String(match.doc_id));
    if (match.doc_name) btn.setAttribute('data-doc-name', match.doc_name);
    // Hover tooltip: name leads, type + status follow as a single
    // meta line. Each part is gated independently so a doc with only
    // partial metadata still gets a useful tooltip rather than a
    // weirdly-truncated separator string.
    const meta = [match.doc_type, match.doc_status_text].filter(Boolean).join(' · ');
    const title = match.doc_name && meta
      ? `${match.doc_name} — ${meta}`
      : (match.doc_name || meta);
    if (title) btn.title = title;
  }
  return btn;
}

export default defineComponent({
  name: 'MarkdownContent',
  components: { ChartView },
  props: {
    text: { type: String, default: '' },
    /**
     * Optional `Map<reference, { doc_id, doc_name }>` that enriches RM
     * reference chips. When a chip's reference is found, the chip's
     * click emits the full `{ reference, doc_id, doc_name }` triple
     * instead of just the reference string — letting consumers route
     * to a doc-summary popover without a second lookup. Unknown refs
     * still produce a working chip, just with null doc fields.
     */
    documentIndex: { type: Map, default: null },
  },
  emits: ['ref-click'],
  setup(props, { emit }) {
    const segments = computed(() => {
      const source = props.text || '';
      const index = props.documentIndex;
      const out = [];

      let lastIndex = 0;
      const re = new RegExp(CHART_BLOCK_PATTERN);
      let match;
      while ((match = re.exec(source)) !== null) {
        if (match.index > lastIndex) {
          const mdText = source.slice(lastIndex, match.index);
          if (mdText.trim().length > 0) {
            out.push({ kind: 'markdown', html: enhanceReferences(md.render(mdText), index) });
          }
        }
        out.push({ kind: 'chart', spec: parseChartSpec(match[1]) });
        lastIndex = match.index + match[0].length;
      }
      if (lastIndex < source.length) {
        const mdText = source.slice(lastIndex);
        if (mdText.trim().length > 0) {
          out.push({ kind: 'markdown', html: enhanceReferences(md.render(mdText), index) });
        }
      }
      if (out.length === 0 && source.length > 0) {
        out.push({ kind: 'markdown', html: enhanceReferences(md.render(source), index) });
      }
      return out;
    });

    function handleClick(e) {
      const target = e.target;
      if (!target) return;
      // Reference chips and rewritten viewdoc links share the click
      // path — both carry data-ref (+ optional doc id/name).
      const chip = target.closest && target.closest('.rm-ref-chip, .rm-doc-link');
      if (!chip) return;
      const ref = chip.getAttribute('data-ref');
      if (!ref) return;
      e.preventDefault();
      const docIdAttr = chip.getAttribute('data-doc-id');
      const docId = docIdAttr != null ? Number(docIdAttr) : null;
      // Pass the click event upstream as the second arg so consumers can
      // anchor a popover (e.g. DocViewPopUp) at the chip element.
      emit(
        'ref-click',
        {
          reference: ref,
          doc_id: Number.isFinite(docId) ? docId : null,
          doc_name: chip.getAttribute('data-doc-name'),
        },
        e,
      );
    }

    function onChildRefClick(refData, event) {
      emit('ref-click', refData, event);
    }

    return { segments, handleClick, onChildRefClick };
  },
});
</script>

<style scoped>
/* Markdown typography — tight spacing for chat context. */
.markdown-content { font-size: 0.875rem; line-height: 1.6; }
.markdown-content >>> p { margin: 0 0 0.5rem 0; }
.markdown-content >>> p:last-child { margin-bottom: 0; }
.markdown-content >>> h1,
.markdown-content >>> h2,
.markdown-content >>> h3,
.markdown-content >>> h4 {
  font-weight: 600;
  margin: 0.75rem 0 0.25rem 0;
  line-height: 1.3;
}
.markdown-content >>> h1 { font-size: 1.15rem; }
.markdown-content >>> h2 { font-size: 1.05rem; }
.markdown-content >>> h3 { font-size: 0.95rem; color: rgb(75 85 99); }
.markdown-content >>> h4 { font-size: 0.9rem; color: rgb(75 85 99); }
.markdown-content >>> ul,
.markdown-content >>> ol { margin: 0.25rem 0 0.5rem 0; padding-left: 1.25rem; }
.markdown-content >>> li { margin: 0.125rem 0; }
.markdown-content >>> ul { list-style-type: disc; }
.markdown-content >>> ol { list-style-type: decimal; }
.markdown-content >>> strong { font-weight: 600; }
/* Brand-pillar emphasis — Architecture / Transparency / Levelling / Skills
   render in the indigo accent, matching the header subtitle lockup. */
.markdown-content >>> strong.atlas-accent-word { color: rgb(79 70 229); }
.markdown-content >>> em { font-style: italic; }
.markdown-content >>> code {
  background-color: rgb(243 244 246);
  padding: 0.1rem 0.3rem;
  border-radius: 0.25rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.85em;
}
.markdown-content >>> pre {
  background-color: rgb(243 244 246);
  padding: 0.5rem 0.75rem;
  border-radius: 0.375rem;
  overflow-x: auto;
  margin: 0.5rem 0;
}
.markdown-content >>> pre code { background-color: transparent; padding: 0; }
.markdown-content >>> blockquote {
  border-left: 3px solid rgb(209 213 219);
  padding-left: 0.75rem;
  margin: 0.5rem 0;
  color: rgb(75 85 99);
}
.markdown-content >>> a {
  color: rgb(79 70 229);
  text-decoration: underline;
  text-underline-offset: 2px;
}
.markdown-content >>> a:hover { color: rgb(67 56 202); }
/* Rewritten viewdoc links — look like links, act like ref chips
   (in-app summary popup instead of browser navigation). */
.markdown-content >>> .rm-doc-link {
  color: rgb(79 70 229);
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
}
.markdown-content >>> .rm-doc-link:hover { color: rgb(67 56 202); }
.markdown-content >>> .rm-doc-link:focus-visible {
  outline: 2px solid rgb(99 102 241);
  outline-offset: 1px;
}
.markdown-content >>> hr {
  border: 0;
  border-top: 1px solid rgb(229 231 235);
  margin: 0.75rem 0;
}

/* RM reference chip — clickable pill rendered inline with the text. */
.markdown-content >>> .rm-ref-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.05rem 0.4rem;
  background-color: rgb(238 242 255);
  color: rgb(67 56 202);
  border: 1px solid rgb(199 210 254);
  border-radius: 0.375rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.82em;
  line-height: 1.4;
  cursor: pointer;
  transition: background-color 0.12s ease, border-color 0.12s ease;
  vertical-align: baseline;
  user-select: none;
  font-weight: 500;
}
.markdown-content >>> .rm-ref-chip:hover {
  background-color: rgb(224 231 255);
  border-color: rgb(129 140 248);
}
.markdown-content >>> .rm-ref-chip:focus-visible {
  outline: 2px solid rgb(99 102 241);
  outline-offset: 1px;
}
</style>
